/*
 * /patient/session — patient full-screen telemedicine view
 * Patient waits for admission, then connects to the Chime meeting.
 */
import { Button, toast } from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import {
	CheckCircle2,
	Clock,
	Layers,
	Loader2,
	MessageSquare,
	Mic,
	MicOff,
	PhoneOff,
	Video,
	VideoOff,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useAuth } from "@emr/auth/client"
import { TelemedicineChatPanel } from "../../components/telemedicine-chat-panel"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"
import {
	DefaultMeetingSession,
	ConsoleLogger,
	LogLevel,
	MeetingSessionConfiguration,
	DefaultDeviceController,
} from "amazon-chime-sdk-js"

export const Route = createFileRoute("/patient/session")({
	component: PatientSessionPage,
	loader: rolePageLoader(["patient"]),
})

/* ─── Background options ─────────────────────────────────────────────────── */

const BACKGROUNDS = [
	{ id: "none",   label: "None",        color: "" },
	{ id: "blur",   label: "Blur",        color: "#d1d5db" },
	{ id: "office", label: "Office",      color: "#1e3a5f" },
	{ id: "nature", label: "Nature",      color: "#14532d" },
	{ id: "plain",  label: "Plain White", color: "#f8fafc" },
]

/* ─── Page ───────────────────────────────────────────────────────────────── */

function PatientSessionPage() {
	const { user } = useAuth()

	const [patientId, setPatientId] = useState<string | null>(null)
	const [sessionId, setSessionId] = useState<string | null>(null)
	const [phase, setPhase] = useState<"loading" | "no_session" | "waiting_room" | "joining" | "active" | "ended">("loading")
	const [micOn, setMicOn]         = useState(true)
	const [camOn, setCamOn]         = useState(true)
	const [elapsed, setElapsed]     = useState(0)
	const [selectedBg, setSelectedBg] = useState("none")
	const [showBgPanel, setShowBgPanel] = useState(false)
	const [showChat, setShowChat]   = useState(false)
	const [hasUnread, setHasUnread] = useState(false)
	const lastSeenMsgCountRef       = useRef(0)

	const meetingSessionRef = useRef<DefaultMeetingSession | null>(null)
	const localVideoRef     = useRef<HTMLVideoElement>(null)
	const remoteVideoRef    = useRef<HTMLVideoElement>(null)
	const elapsedRef        = useRef<ReturnType<typeof setInterval> | null>(null)
	const admittedRef       = useRef(false)

	const { data: me } = trpc.patient.getByFilter.useQuery({ where: { active: true } })

	useEffect(() => {
		if (me?.id) setPatientId(me.id)
	}, [me?.id])

	// Poll for new chat messages while chat is closed to show the unread dot
	const { data: chatPreview } = trpc.telemedicineChat.getBySession.useQuery(
		{ sessionId: sessionId ?? "" },
		{ enabled: !showChat && !!sessionId && phase !== "loading" && phase !== "no_session", refetchInterval: 4000 },
	)

	useEffect(() => {
		if (!chatPreview) return
		const nonMine = chatPreview.filter((m) => m.senderId !== user?.id)
		if (nonMine.length > lastSeenMsgCountRef.current) setHasUnread(true)
	}, [chatPreview, user?.id])

	function openChat() {
		setShowChat(true)
		setHasUnread(false)
		lastSeenMsgCountRef.current = chatPreview?.filter((m) => m.senderId !== user?.id).length ?? 0
	}

	/* ─── Helper functions (declared before mutations that call them) ────── */

	function startElapsed() {
		if (elapsedRef.current) clearInterval(elapsedRef.current)
		setElapsed(0)
		elapsedRef.current = setInterval(() => setElapsed((s) => s + 1), 1000)
	}

	function disconnectChime() {
		if (meetingSessionRef.current) {
			try {
				const av = meetingSessionRef.current.audioVideo
				av.stopLocalVideoTile()
				av.stopVideoInput()
				av.stopAudioInput()
				av.stop()
			} catch {}
			meetingSessionRef.current = null
		}
		if (elapsedRef.current) {
			clearInterval(elapsedRef.current)
			elapsedRef.current = null
		}
	}

	async function connectChime(meetingData: any, attendeeData: any) {
		const logger = new ConsoleLogger("TeleMed-Patient", LogLevel.WARN)
		const deviceController = new DefaultDeviceController(logger)
		const config = new MeetingSessionConfiguration(meetingData, attendeeData)
		const ms = new DefaultMeetingSession(config, logger, deviceController)
		meetingSessionRef.current = ms

		const videoDevices = await ms.audioVideo.listVideoInputDevices()
		const audioDevices = await ms.audioVideo.listAudioInputDevices()
		if (videoDevices[0]) await ms.audioVideo.startVideoInput(videoDevices[0].deviceId)
		if (audioDevices[0]) await ms.audioVideo.startAudioInput(audioDevices[0].deviceId)

		ms.audioVideo.addObserver({
			videoTileDidUpdate(tileState) {
				if (tileState.localTile) {
					ms.audioVideo.bindVideoElement(tileState.tileId!, localVideoRef.current!)
				} else if (tileState.boundAttendeeId) {
					ms.audioVideo.bindVideoElement(tileState.tileId!, remoteVideoRef.current!)
				}
			},
			audioVideoDidStop() {
				setPhase("ended")
			},
		})

		ms.audioVideo.start()
		ms.audioVideo.startLocalVideoTile()
	}

	/* ─── Mutations ──────────────────────────────────────────────────────── */

	const patientJoin = trpc.telemedicine.patientJoin.useMutation()
	const end = trpc.telemedicine.end.useMutation({
		onSuccess: () => {
			disconnectChime()
			setPhase("ended")
		},
	})

	/* ─── Polling ────────────────────────────────────────────────────────── */

	const { data: activeSession } = trpc.telemedicine.getActiveByPatient.useQuery(
		{ patientId: patientId! },
		{
			enabled: !!patientId && (phase === "loading" || phase === "waiting_room" || phase === "no_session"),
			refetchInterval: 3000,
		},
	)

	useEffect(() => {
		if (!activeSession) {
			if (phase === "loading") setPhase("no_session")
			return
		}

		if (phase === "loading" || phase === "no_session") {
			setSessionId(activeSession.id)
			setPhase("waiting_room")
			if (activeSession.status === "waiting_patient") {
				patientJoin.mutate({ sessionId: activeSession.id })
			}
		}

		if (
			activeSession.status === "active" &&
			activeSession.patientAttendeeData &&
			activeSession.chimeMeetingData &&
			!admittedRef.current
		) {
			admittedRef.current = true
			setPhase("joining")
			connectChime(
				JSON.parse(activeSession.chimeMeetingData),
				JSON.parse(activeSession.patientAttendeeData),
			).then(() => {
				setPhase("active")
				startElapsed()
			}).catch((err) => {
				toast.error("Failed to connect to session")
				console.error(err)
				setPhase("waiting_room")
				admittedRef.current = false
			})
		}
	}, [activeSession?.status, activeSession?.patientAttendeeData, phase])

	useEffect(() => () => {
		if (elapsedRef.current) clearInterval(elapsedRef.current)
		disconnectChime()
	}, [])

	/* ─── Control handlers ───────────────────────────────────────────────── */

	function toggleMic() {
		const ms = meetingSessionRef.current
		if (!ms) return
		if (micOn) ms.audioVideo.realtimeMuteLocalAudio()
		else ms.audioVideo.realtimeUnmuteLocalAudio()
		setMicOn((v) => !v)
	}

	function toggleCam() {
		const ms = meetingSessionRef.current
		if (!ms) return
		if (camOn) ms.audioVideo.stopLocalVideoTile()
		else ms.audioVideo.startLocalVideoTile()
		setCamOn((v) => !v)
	}

	function handleEnd() {
		if (!sessionId) return
		end.mutate({ sessionId })
	}

	const canChat = !!sessionId && phase !== "loading" && phase !== "no_session" && phase !== "ended"

	return (
		<div className="fixed inset-0 z-50 bg-gray-950 flex flex-col">
			{/* Header */}
			<div className="flex items-center justify-between px-6 py-4 bg-gray-900 border-b border-gray-800 shrink-0">
				<div className="flex items-center gap-3">
					<Video className="w-5 h-5 text-primary" />
					<div>
						<p className="text-sm font-semibold text-white">Telemedicine Session</p>
						<p className="text-xs text-gray-400">
							{phase === "loading"      && "Checking for active sessions…"}
							{phase === "no_session"   && "No active session"}
							{phase === "waiting_room" && "Waiting for practitioner to admit you"}
							{phase === "joining"      && "Connecting…"}
							{phase === "active"       && `In session · ${formatDuration(elapsed)}`}
							{phase === "ended"        && "Session ended"}
						</p>
					</div>
				</div>
				{phase === "active" && (
					<div className="flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-green-900/60 text-green-300">
						<span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
						Live
					</div>
				)}
			</div>

			{/* Body (video + optional chat panel) */}
			<div className="flex-1 flex min-h-0">
				{/* Video area */}
				<div className="flex-1 relative bg-black overflow-hidden">
					{/* Remote video (practitioner) */}
					<video
						ref={remoteVideoRef}
						autoPlay
						playsInline
						className="absolute inset-0 w-full h-full object-cover"
						style={{ display: phase === "active" ? "block" : "none" }}
					/>

					{/* Non-active overlays */}
					{phase !== "active" && (
						<div className="absolute inset-0 flex flex-col items-center justify-center gap-6 text-center px-8">
							{phase === "loading" && <Loader2 className="w-12 h-12 text-primary animate-spin" />}

							{phase === "no_session" && (
								<>
									<div className="w-24 h-24 rounded-full bg-gray-800 flex items-center justify-center">
										<Clock className="w-12 h-12 text-gray-600" />
									</div>
									<p className="text-gray-300 text-lg font-medium">No active session</p>
									<p className="text-gray-500 text-sm max-w-sm">
										Your practitioner hasn't started a session yet. You'll receive a notification when they're ready.
									</p>
									<Button variant="outline" onClick={() => window.history.back()}>Go Back</Button>
								</>
							)}

							{phase === "waiting_room" && (
								<>
									<div className="w-24 h-24 rounded-full bg-blue-900/30 flex items-center justify-center">
										<Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
									</div>
									<p className="text-gray-200 text-xl font-semibold">You're in the waiting room</p>
									<p className="text-gray-400 text-sm max-w-sm">
										Your practitioner will admit you shortly. You can use the chat to send a message while you wait.
									</p>
									<div className="mt-4 rounded-xl overflow-hidden border border-gray-700 shadow-xl">
										<LocalCameraPreview />
									</div>
								</>
							)}

							{phase === "joining" && (
								<>
									<Loader2 className="w-12 h-12 text-green-400 animate-spin" />
									<p className="text-gray-300 text-lg">Connecting to session…</p>
								</>
							)}

							{phase === "ended" && (
								<>
									<div className="w-24 h-24 rounded-full bg-gray-800 flex items-center justify-center">
										<CheckCircle2 className="w-12 h-12 text-gray-500" />
									</div>
									<p className="text-gray-300 text-lg font-medium">Session ended</p>
									<Button variant="outline" onClick={() => window.history.back()}>Go Back</Button>
								</>
							)}
						</div>
					)}

					{/* Local video PiP */}
					<video
						ref={localVideoRef}
						autoPlay
						playsInline
						muted
						className="absolute bottom-6 right-6 w-44 h-32 rounded-xl object-cover border-2 border-gray-700 bg-gray-800 shadow-xl"
						style={{ display: phase === "active" ? "block" : "none" }}
					/>

					{/* Background panel */}
					{showBgPanel && (
						<div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-gray-900/95 backdrop-blur rounded-2xl p-4 flex items-center gap-3 border border-gray-700 shadow-2xl">
							<Layers className="w-4 h-4 text-gray-400" />
							<span className="text-xs text-gray-400 mr-1">Background</span>
							{BACKGROUNDS.map((bg) => (
								<button
									key={bg.id}
									onClick={() => { setSelectedBg(bg.id); setShowBgPanel(false) }}
									className={`w-10 h-7 rounded-lg border-2 text-xs transition-all ${selectedBg === bg.id ? "border-primary scale-110" : "border-gray-600 hover:border-gray-400"}`}
									style={{ backgroundColor: bg.color || "#374151" }}
									title={bg.label}
								/>
							))}
						</div>
					)}
				</div>

				{/* Chat panel (right side, available from waiting room onward) */}
				{showChat && sessionId && (
					<TelemedicineChatPanel
						sessionId={sessionId}
						myUserId={user?.id}
						onClose={() => setShowChat(false)}
						className="w-80 h-full"
					/>
				)}
			</div>

			{/* Controls — shown when in active call */}
			{phase === "active" && (
				<div className="flex items-center justify-center gap-4 py-5 bg-gray-900 border-t border-gray-800 shrink-0">
					<ControlBtn active={micOn} onClick={toggleMic} icon={<Mic className="w-5 h-5" />} offIcon={<MicOff className="w-5 h-5" />} label="Mic" />
					<ControlBtn active={camOn} onClick={toggleCam} icon={<Video className="w-5 h-5" />} offIcon={<VideoOff className="w-5 h-5" />} label="Camera" />
					<ControlBtn active={showBgPanel} onClick={() => setShowBgPanel((v) => !v)} icon={<Layers className="w-5 h-5" />} offIcon={<Layers className="w-5 h-5" />} label="Background" />
					{/* Chat toggle */}
					<button onClick={showChat ? () => setShowChat(false) : openChat} className="flex flex-col items-center gap-1 relative">
						<span className={`w-14 h-14 rounded-full flex items-center justify-center text-white transition-colors ${showChat ? "bg-primary hover:bg-primary/90" : "bg-gray-700 hover:bg-gray-600"}`}>
							<MessageSquare className="w-5 h-5" />
						</span>
						{hasUnread && !showChat && (
							<span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-gray-900" />
						)}
						<span className="text-xs text-gray-400">Chat</span>
					</button>
					<button onClick={handleEnd} disabled={end.isPending} className="flex flex-col items-center gap-1">
						<span className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center text-white transition-colors">
							<PhoneOff className="w-6 h-6" />
						</span>
						<span className="text-xs text-gray-400">Leave</span>
					</button>
				</div>
			)}

			{/* Waiting room chat toggle bar (available before active call) */}
			{canChat && phase !== "active" && (
				<div className="flex items-center justify-center gap-4 py-3 bg-gray-900 border-t border-gray-800 shrink-0">
					<button onClick={showChat ? () => setShowChat(false) : openChat} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium relative transition-colors bg-gray-800 hover:bg-gray-700 text-white">
						<MessageSquare className="w-4 h-4" />
						{showChat ? "Hide Chat" : "Open Chat"}
						{hasUnread && !showChat && (
							<span className="w-2 h-2 bg-red-500 rounded-full" />
						)}
					</button>
				</div>
			)}
		</div>
	)
}

/* ─── Local camera preview (waiting room) ───────────────────────────────── */

function LocalCameraPreview() {
	const videoRef = useRef<HTMLVideoElement>(null)
	useEffect(() => {
		let stream: MediaStream | null = null
		navigator.mediaDevices.getUserMedia({ video: true, audio: false })
			.then((s) => {
				stream = s
				if (videoRef.current) videoRef.current.srcObject = s
			})
			.catch(() => {})
		return () => {
			stream?.getTracks().forEach((t) => t.stop())
		}
	}, [])
	return <video ref={videoRef} autoPlay playsInline muted className="w-64 h-48 object-cover bg-gray-800" />
}

/* ─── Shared helpers ─────────────────────────────────────────────────────── */

function ControlBtn({
	active,
	onClick,
	icon,
	offIcon,
	label,
}: {
	active: boolean
	onClick: () => void
	icon: React.ReactNode
	offIcon: React.ReactNode
	label: string
}) {
	return (
		<button onClick={onClick} className="flex flex-col items-center gap-1">
			<span className={`w-14 h-14 rounded-full flex items-center justify-center text-white transition-colors ${active ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-800/80 hover:bg-gray-700 text-gray-400"}`}>
				{active ? icon : offIcon}
			</span>
			<span className="text-xs text-gray-400">{label}</span>
		</button>
	)
}

function formatDuration(s: number) {
	const m = Math.floor(s / 60)
	const ss = s % 60
	return `${m}:${String(ss).padStart(2, "0")}`
}
